/* @jsx h */
// @ts-nocheck
import { defineComponent, h, ref, computed, onMounted, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { DateSelector } from "../components/DateSelector";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth } from "@app/vue/providers/Auth";
import { listReservations } from "../utils/trpcReservations";
import { ReservationDetails } from "./ReservationsSchedule/components/ReservationDetails";
import { Reservation } from "@lemmonhq/types";

export const ReservationsListPage = defineComponent({
  name: "ReservationsListPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const restaurantTimeZone = computed(
      () => auth.user.value?.restaurant.timezone || "UTC",
    );
    const selectedDate = ref(new Date());
    const showDetailsSheet = ref(false);
    const selectedReservation = ref<Reservation>();
    const reservations = ref<Reservation[]>([]);
    const loading = ref(true);
    const error = ref("");

    const statusColors: Record<string, string> = {
      confirmed: "bg-blue-500/10 text-blue-500",
      seated: "bg-yellow-500/10 text-yellow-500",
      "partially-seated": "bg-orange-500/10 text-orange-500",
      completed: "bg-green-500/10 text-green-500",
      "no-show": "bg-red-500/10 text-red-500",
      cancelled: "bg-gray-500/10 ",
    };

    const loadData = async () => {
      try {
        loading.value = true;
        error.value = "";

        const result = await listReservations(trpc, {
          date: selectedDate.value,
          timezone: restaurantTimeZone.value,
          limit: "100",
        });

        reservations.value = result.data || [];
      } catch (e) {
        console.error("Failed to load reservations:", e);
        error.value = "Failed to load reservations";
        reservations.value = [];
      } finally {
        loading.value = false;
      }
    };

    const sortedReservations = computed(() => {
      return [...reservations.value].sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
      );
    });

    const handleReservationClick = (reservation: Reservation) => {
      selectedReservation.value = reservation;
      showDetailsSheet.value = true;
    };

    const handleReservationUpdate = (updatedReservation: Reservation) => {
      // Update reservation in place
      const index = reservations.value.findIndex(
        (r) => r.id === updatedReservation.id,
      );
      if (index !== -1) {
        reservations.value[index] = updatedReservation;
      }
      loadData();
    };

    const handleReservationCancel = (cancelledReservation: Reservation) => {
      // Remove the cancelled reservation from the list
      const index = reservations.value.findIndex(
        (r) => r.id === cancelledReservation.id,
      );
      if (index !== -1) {
        reservations.value = reservations.value.filter(
          (r) => r.id !== cancelledReservation.id,
        );
      }
      loadData();
    };

    watch(selectedDate, () => {
      loadData();
    });

    onMounted(() => {
      loadData();
    });

    return () => (
      <div class="flex flex-col overflow-hidden flex-1 h-full bg-base-300/30">
        {/* Header */}
        <div class="bg-base-100 border-b border-base-300">
          <div class="px-6 py-5">
            <div class="flex justify-between items-center">
              <div>
                <h1 class="text-2xl font-semibold text-accent tracking-tight flex items-center gap-3">
                  <div class="w-8 h-8 text-center flex justify-center items-center flex bg-primary/10 rounded-lg">
                    <span class="material-symbols-outlined text-2xl text-primary">
                      list
                    </span>
                  </div>
                  {t("reservations.general.reservations")}
                </h1>
                <p class="text-sm text-base-content/60 mt-1 ml-12"></p>
              </div>
              <div class="flex items-center gap-3">
                <DateSelector
                  selectedDate={selectedDate.value}
                  onDateChange={(date) => (selectedDate.value = date)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div class="flex flex-col flex-1 p-6 min-h-0 overflow-y-auto">
          {loading.value ? (
            <div class="flex items-center justify-center h-full">
              <div class="text-center">
                <span class="loading loading-spinner loading-lg text-primary"></span>
                <p class="mt-4 text-sm text-base-content/60">
                  {t("reservations.general.loading_reservations")}
                </p>
              </div>
            </div>
          ) : error.value ? (
            <div class="flex items-center justify-center h-full">
              <div class="text-center bg-base-100 rounded-xl p-8 shadow-sm">
                <div class="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mx-auto mb-4">
                  <span class="material-symbols-outlined text-error text-2xl">
                    error
                  </span>
                </div>
                <h3 class="font-medium text-accent mb-2">
                  {t("reservations.general.error_loading")}
                </h3>
                <p class="text-sm text-base-content/60 mb-4">{error.value}</p>
                <button
                  onClick={() => loadData()}
                  class="btn btn-primary btn-sm"
                >
                  {t("reservations.general.try_again")}
                </button>
              </div>
            </div>
          ) : sortedReservations.value.length === 0 ? (
            <div class="flex items-center justify-center h-full">
              <div class="text-center">
                <div class="w-16 h-16 rounded-full bg-base-200 flex items-center justify-center mx-auto mb-4">
                  <span class="material-symbols-outlined text-base-content/30 text-3xl">
                    event_busy
                  </span>
                </div>
                <h3 class="font-medium text-accent mb-1">
                  {t("reservations.general.no_reservations")}
                </h3>
                <p class="text-sm text-base-content/60">
                  {t("reservations.general.no_reservations_date")}
                </p>
              </div>
            </div>
          ) : (
            <div class="grid gap-2">
              {sortedReservations.value.map((reservation) => {
                const reservationDate = new Date(reservation.time);
                const dayOfWeek = reservationDate.toLocaleDateString("en-US", {
                  weekday: "short",
                });
                const timeStr = reservationDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });

                return (
                  <div
                    key={reservation.id}
                    onClick={() => handleReservationClick(reservation)}
                    class="bg-base-100 rounded-lg p-4 hover:bg-base-200/50 cursor-pointer transition-all border border-base-300/50 hover:border-primary/30 group"
                  >
                    <div class="flex items-start gap-4">
                      {/* Day + Time Section */}
                      <div class="flex flex-col items-center justify-center min-w-[70px] pt-1">
                        <div class="text-xs text-base-content/50 font-medium uppercase tracking-wide">
                          {dayOfWeek}
                        </div>
                        <div class="text-xl font-semibold text-accent tabular-nums mt-0.5">
                          {timeStr}
                        </div>
                      </div>

                      {/* Party Info Section */}
                      <div class="flex-1 min-w-0">
                        <div class="flex items-start justify-between gap-3">
                          <div class="flex-1 min-w-0">
                            <h3 class="font-semibold text-accent text-base leading-tight truncate">
                              {reservation.party_name} •{" "}
                              {reservation.party_size}{" "}
                              {reservation.party_size === 1
                                ? t("reservations.general.guest")
                                : t("reservations.general.guests")}
                            </h3>
                            {reservation.notes && (
                              <div class="flex items-center gap-2 mt-1">
                                <span class="text-sm text-base-content/60 truncate">
                                  {reservation.notes}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Table Assignments Section */}
                      <div class="flex items-center min-w-[120px] justify-end">
                        {reservation.table_assignments?.length > 0 ? (
                          <div class="flex flex-col gap-1">
                            {reservation.table_assignments.map((assignment) => (
                              <div
                                key={assignment.id}
                                class="flex items-center justify-between bg-purple-200 border-2 border-purple-600 rounded px-2 py-1"
                              >
                                <div class="flex items-center gap-1">
                                  <span class="material-symbols-outlined text-xs text-purple-800">
                                    table_restaurant
                                  </span>
                                  <span class="text-xs text-purple-900 font-medium">
                                    {assignment.table_name}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div class="text-sm text-base-content/40 italic">
                            {t("reservations.general.no_tables")}
                          </div>
                        )}
                      </div>

                      {/* Status Section */}
                      <div class="flex items-center min-w-[100px] justify-end">
                        <span
                          class={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                            statusColors[reservation.status] ||
                            "bg-base-300 text-base-content"
                          }`}
                        >
                          {reservation.status
                            ?.replace("-", " ")
                            .replace("_", " ")
                            .toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showDetailsSheet.value && selectedReservation.value && (
          <ReservationDetails
            reservation={selectedReservation.value}
            onClose={() => (showDetailsSheet.value = false)}
            onUpdate={handleReservationUpdate}
            onReservationCancel={handleReservationCancel}
          />
        )}
      </div>
    );
  },
});
